import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { Link, Toast } from 'types/layout';
import { RouteLocationNormalized, Router } from 'vue-router';
import { attachOAuthTokenRefreshInterceptor } from '@utils/interceptors/oauth-interceptor-response';
import { attachWafChallengeCaptchaInterceptor } from '@utils/interceptors/waf-challenge-captcha-interceptor';
import { correlationIdHeaderInterceptor } from '@utils/interceptors/correlation-id-interceptor-request';
import { errorHandlerInterceptor } from '@utils/interceptors/error-handler-interceptor';
import { forceCorsGetRequestInterceptor } from '@utils/interceptors/force-cors-get-request';
import { ldUserHeaderInterceptor } from '@utils/interceptors/ld-user-interceptor-request';
import { oAuthTokenHeaderInterceptor } from '@utils/interceptors/oauth-interceptor-request';
import { passwordResetKeyHeaderInterceptor } from '@utils/interceptors/password-reset-key-interceptor-request';
import { passwordResetSessionUuidHeaderInterceptor } from '@utils/interceptors/password-reset-session-uuid-interceptor-request';
import { useAdvisorStore } from '@stores/advisor';
import { useAppStore } from '@stores/app';
import { useInvestmentEntityStore } from '@stores/investment-entity';
import { wafTokenRequestInterceptor } from '@utils/interceptors/waf-token-request';

function getApiBaseUrl(): string {
	const appStore = useAppStore();
	return appStore.isMobileWebview
		? `${import.meta.env.VITE_BASE_MOBILE_API_URL}`
		: `${import.meta.env.VITE_BASE_API_URL}`;
}

function getOAuthBaseUrl(): string {
	const appStore = useAppStore();
	return appStore.isMobileWebview
		? `${import.meta.env.VITE_BASE_OAUTH_MOBILE_API_URL}`
		: `${import.meta.env.VITE_BASE_OAUTH_API_URL}`;
}

function createAxiosInstance(
	isAuthenticated: boolean,
	requestType: 'oauth' | 'api',
	requestConfig?: AxiosRequestConfig
): AxiosInstance {
	const instance = axios.create({ ...requestConfig, withCredentials: true });

	instance.interceptors.request.use((config) => {
		if (!config.isRetry) {
			const baseUrl = requestType === 'oauth' ? getOAuthBaseUrl() : getApiBaseUrl();
			const basePath = config.baseURL ?? '';
			config.baseURL = `${baseUrl}${basePath}`;
		}

		return config;
	});

	if (isAuthenticated) {
		addAuthenticatedInterceptors(instance);
	}

	addDefaultInterceptors(instance);

	return instance;
}

function addAuthenticatedInterceptors(instance: AxiosInstance): void {
	instance.interceptors.request.use(...oAuthTokenHeaderInterceptor);
	instance.interceptors.request.use(...correlationIdHeaderInterceptor);
	attachOAuthTokenRefreshInterceptor(instance);
}

function addDefaultInterceptors(instance: AxiosInstance): void {
	instance.interceptors.request.use(...forceCorsGetRequestInterceptor);
	instance.interceptors.request.use(...wafTokenRequestInterceptor);
	attachWafChallengeCaptchaInterceptor(instance);
	instance.interceptors.response.use(...errorHandlerInterceptor);
	instance.interceptors.request.use(...ldUserHeaderInterceptor);
}

declare module 'axios' {
	export interface AxiosRequestConfig {
		errorToast?: Toast;
		suppressToast?: (response: AxiosResponse) => boolean;
		isRetry?: boolean;
		wafChallengeRetryCount?: number;
	}

	export type AxiosRequestInterceptorParams = Parameters<AxiosInterceptorManager<AxiosRequestConfig>['use']>;
	export type AxiosResponseInterceptorParams = Parameters<AxiosInterceptorManager<AxiosResponse>['use']>;
}

export const apiSecureRequest = createAxiosInstance(true, 'api', {
	baseURL: `/api-secured`
});

export const investmentEntityRequest = createAxiosInstance(true, 'api', {});

investmentEntityRequest.interceptors.request.use(
	(config) => {
		const advisorStore = useAdvisorStore();
		const appStore = useAppStore();
		const investmentEntityStore = useInvestmentEntityStore();
		if (!config.isRetry) {
			config.baseURL = `/api-secured/investment-entity/${
				appStore.isAdvisor
					? advisorStore.selectedInvestmentEntityId
					: `${investmentEntityStore.investmentEntityId}`
			}`;
		}

		return config;
	},
	(error: AxiosError) => Promise.reject(error)
);

export const investmentEntityApsRequest = createAxiosInstance(true, 'api', {});

investmentEntityApsRequest.interceptors.request.use(
	(config) => {
		const advisorStore = useAdvisorStore();
		const appStore = useAppStore();
		const investmentEntityStore = useInvestmentEntityStore();
		if (!config.isRetry) {
			config.baseURL = `/api-secured/aps/investment-entity/${
				appStore.isAdvisor ? advisorStore.selectedInvestmentEntityId : investmentEntityStore.investmentEntityId
			}`;
		}
		return config;
	},
	(error: AxiosError) => Promise.reject(error)
);

export const oauthSecureRequest = createAxiosInstance(true, 'oauth', {
	baseURL: `/api-secured/authentication`
});

export const forgotPasswordApiRequest = createAxiosInstance(false, 'api', {
	baseURL: `/api`
});
forgotPasswordApiRequest.interceptors.request.use(...passwordResetKeyHeaderInterceptor);
forgotPasswordApiRequest.interceptors.request.use(...passwordResetSessionUuidHeaderInterceptor);
forgotPasswordApiRequest.interceptors.request.use(...correlationIdHeaderInterceptor);

export const oauthTokenRequest = createAxiosInstance(false, 'oauth', {
	auth: {
		username: `${import.meta.env.VITE_API_CLIENT_ID}`,
		password: `${import.meta.env.VITE_OAUTH_CLIENT_SECRET}`
	}
});

export const oauthRequest = createAxiosInstance(false, 'oauth', {});

export const apiRequest = createAxiosInstance(false, 'api', {
	baseURL: `/api`
});

export const constructUrlWithQueryParams = (url: string, params: object): string => {
	const initialSeparator = url.indexOf('?') === -1 ? '?' : '&';
	const entries = Object.entries(params);
	const paramArray: Array<string> = [];

	for (const [key, val] of entries) {
		paramArray.push(`${key}=${val}`);
	}

	return `${url + initialSeparator + paramArray.join('&')}`;
};

export const flattenQueryParam = (queryParam: string | (string | null)[] | null): string | null => {
	if (queryParam === null) {
		return null;
	}

	return Array.isArray(queryParam) ? queryParam[0] : queryParam;
};

export const includesRequiredQueryParams = (requiredParams: Array<string>, to: RouteLocationNormalized): boolean => {
	return requiredParams.every((param) => Object.keys(to.query).includes(param));
};

export function getLinkFromRoute(router: Router, routePath: string, linkText: string): Link {
	const routeResolution = router.resolve(routePath);
	if (routeResolution.matched.length > 0 && routeResolution.name) {
		return {
			router: routeResolution.name as string,
			text: linkText,
			params: routeResolution.params,
			query: routeResolution.query
		};
	} else {
		const startOfPathOptions = ['https://', 'http://', 'mailto:', '/'];
		if (startOfPathOptions.some((option) => routePath.startsWith(option))) {
			return { href: routePath, text: linkText };
		} else {
			return { href: `/${routePath}`, text: linkText };
		}
	}
}

export function triggerDownload(url: string): void {
	const anchor = document.createElement('a');
	anchor.style.display = 'none';
	document.body.appendChild(anchor);
	anchor.href = url;
	anchor.download = 'true';
	anchor.click();
	document.body.removeChild(anchor);
}
